import { z } from 'zod'
import {
  type App,
  type TotemApp,
  type DesktopApp,
  AppKind,
  PushNotificationStatus,
  type OrdersheetApp,
  type PdvMobileApp,
  type ServerApp,
  type MobileApp,
  type DigitalMenuApp,
  type PosTefApp,
  type OtherApp,
  type SmartMenuApp,
  type StoreApp,
  type KdsMobileApp
} from '../../models/App'
import {
  DigitalMenuAppSettingDTO,
  mapDTOToDigitalMenuAppSetting,
  mapDigitalMenuAppSettingToDTO
} from './DigitalMenuAppSettingDTO'
import { TotemAppSettingDTO, mapDTOToTotemAppSetting, mapTotemAppSettingToDTO } from './TotemAppSettingDTO'
import { PdvMobileAppSettingDTO, mapPdvMobileAppSettingToDTO, mapDTOToPdvMobileAppSetting } from './PdvMobileAppSettingDTO'
import { DesktopAppSettingDTO, mapDTOToDesktopAppSetting, mapDesktopAppSettingToDTO } from './DesktopAppSettingDTO'
import { ServerAppDTO } from './ServerAppDTO'
import { ServerAppSettingDTO, mapDTOToServerAppSetting, mapServerAppSettingToDTO } from './ServerAppSettingDTO'
import { mapDTOToMobileAppSetting, mapMobileAppSettingToDTO, MobileAppSettingDTO } from './MobileAppSettingDTO'
import { StoreAppSettingDTOSchema, mapDTOToStoreAppSetting, mapStoreAppSettingToDTO } from './StoreAppSettingDTO'
import { SmartMenuAppSettingDTO, mapDTOToSmartMenuAppSetting, mapSmartMenuAppSettingToDTO } from './SmartMenuAppSettingDTO'
import { KdsMobileAppSettingDTO, mapDTOToKdsMobileAppSetting, mapKdsMobileAppSettingToDTO } from './KdsMobileAppSettingDTO'
import { resolveStoreAppInternalName } from '~/domain/dashboard/store/utils/resolveStoreAppInternalName'

// PHP json_encode([]) vira JSON array. Settings vazio na API deve ser null.
export const EmptySettingsArray = z.array(z.unknown()).length(0).transform((): null => null)

export function nullableAppSettings<T extends z.ZodTypeAny> (
  schema: T
): z.ZodType<z.output<T> | null> {
  return z.union([schema, z.null(), EmptySettingsArray]) as z.ZodType<z.output<T> | null>
}

const CardContractDTO = z.object({
  id: z.number().optional(),
  name: z.string()
})

const AppDeviceDTO = z.object({
  model: z.string()
})

export const AppBaseDTO = z.object({
  id: z.number().optional(),
  name: z.string(),
  image_url: z.string().nullish(),
  photo_base64: z.string().optional(),
  card_contracts: z.array(CardContractDTO).optional(),
  terminal: z.string().nullish(),
  device: AppDeviceDTO.nullish(),
  push_notification_status: z.nativeEnum(PushNotificationStatus).nullish(),
  was_base_product_auto_enabled_for_sale: z.boolean().optional()
})

const DigitalMenuAppDTO = AppBaseDTO.extend({
  type: z.enum([AppKind.DigitalMenu]),
  settings: nullableAppSettings(DigitalMenuAppSettingDTO)
})

const TotemAppDTO = AppBaseDTO.extend({
  type: z.enum([AppKind.Totem]),
  settings: nullableAppSettings(TotemAppSettingDTO)
})

const DesktopAppDTO = AppBaseDTO.extend({
  type: z.enum([AppKind.Desktop]),
  settings: nullableAppSettings(DesktopAppSettingDTO)
})

const OrdersheetAppDTO = AppBaseDTO.extend({
  type: z.enum([AppKind.Ordersheet])
})

const PdvMobileAppDTO = AppBaseDTO.extend({
  type: z.enum([AppKind.PdvMobile]),
  settings: nullableAppSettings(PdvMobileAppSettingDTO)
})

const MobileAppDTO = AppBaseDTO.extend({
  type: z.enum([AppKind.Mobile]),
  settings: nullableAppSettings(MobileAppSettingDTO)
})

const PosTefAppDTO = AppBaseDTO.extend({
  type: z.enum([AppKind.PosTef])
})

const SmartMenuAppDTO = AppBaseDTO.extend({
  type: z.enum([AppKind.SmartMenu]),
  settings: nullableAppSettings(SmartMenuAppSettingDTO)
})

const OtherAppDTO = AppBaseDTO.extend({
  type: z.enum([AppKind.Other])
})

export type AppCatalogCategoryDTOType = {
  category?: { id: number; name: string }
  product_category_id?: number
  preparation_time?: number
  subcategories?: AppCatalogCategoryDTOType[]
}

const AppCatalogCategoryDTO: z.ZodType<AppCatalogCategoryDTOType> = z.lazy(() =>
  z.object({
    category: z.object({
      id: z.number(),
      name: z.string()
    }).optional(),
    product_category_id: z.number().optional(),
    preparation_time: z.number().optional(),
    subcategories: z.array(AppCatalogCategoryDTO).optional()
  })
)

const StoreAppDTO = AppBaseDTO.extend({
  type: z.enum([AppKind.Store]),
  settings: nullableAppSettings(StoreAppSettingDTOSchema),
  domain: z.string().nullish(),
  domain_editable: z.boolean().optional(),
  categories: z.array(AppCatalogCategoryDTO).optional()
})

const KdsMobileAppDTO = AppBaseDTO.extend({
  type: z.enum([AppKind.KdsMobile]),
  settings: nullableAppSettings(KdsMobileAppSettingDTO)
})

// ServerAppDTO é importado separado para evitar dependência circular
export const AppDTO = z.discriminatedUnion('type', [
  DigitalMenuAppDTO,
  TotemAppDTO,
  ServerAppDTO.extend({
    settings: nullableAppSettings(ServerAppSettingDTO).optional()
  }),
  DesktopAppDTO,
  OrdersheetAppDTO,
  PdvMobileAppDTO,
  MobileAppDTO,
  PosTefAppDTO,
  SmartMenuAppDTO,
  OtherAppDTO,
  StoreAppDTO,
  KdsMobileAppDTO
])

export type AppDTO = z.infer<typeof AppDTO>
export type DigitalMenuAppDTO = z.infer<typeof DigitalMenuAppDTO>
export type TotemAppDTO = z.infer<typeof TotemAppDTO>
export type DesktopAppDTO = z.infer<typeof DesktopAppDTO>
export type OrdersheetAppDTO = z.infer<typeof OrdersheetAppDTO>
export type PdvMobileAppDTO = z.infer<typeof PdvMobileAppDTO>
export type MobileAppDTO = z.infer<typeof MobileAppDTO>
export type PosTefAppDTO = z.infer<typeof PosTefAppDTO>
export type SmartMenuAppDTO = z.infer<typeof SmartMenuAppDTO>
export type OtherAppDTO = z.infer<typeof OtherAppDTO>
export type StoreAppDTO = z.infer<typeof StoreAppDTO>
export type KdsMobileAppDTO = z.infer<typeof KdsMobileAppDTO>

export function mapAppToDTO (model: App): AppDTO {
  const baseMapping = {
    id: model.id,
    name: model.name,
    image_url: model.imageUrl ?? null,
    photo_base64: model.photoBase64,
    card_contracts: model.cardContracts?.map(contract => ({
      id: contract.id,
      name: contract.name
    })),
    terminal: model.terminal ?? null,
    device: model.device ?? null,
    push_notification_status: model.pushNotificationStatus,
    was_base_product_auto_enabled_for_sale: model.wasBaseProductAutoEnabledForSale
  }

  switch (model.kind) {
    case AppKind.DigitalMenu:
      return {
        ...baseMapping,
        type: AppKind.DigitalMenu,
        settings: mapDigitalMenuAppSettingToDTO(model.settings)
      }
    case AppKind.Totem:
      return {
        ...baseMapping,
        type: AppKind.Totem,
        settings: mapTotemAppSettingToDTO(model.settings)
      }
    case AppKind.Server:
      return {
        ...baseMapping,
        type: AppKind.Server,
        settings: mapServerAppSettingToDTO(model.settings)
      }
    case AppKind.Desktop:
      return {
        ...baseMapping,
        type: AppKind.Desktop,
        settings: mapDesktopAppSettingToDTO(model.settings)
      }
    case AppKind.Ordersheet:
      return {
        ...baseMapping,
        type: AppKind.Ordersheet
      }
    case AppKind.PdvMobile:
      return {
        ...baseMapping,
        type: AppKind.PdvMobile,
        settings: mapPdvMobileAppSettingToDTO(model.settings)
      }
    case AppKind.Mobile:
      return {
        ...baseMapping,
        type: AppKind.Mobile,
        settings: mapMobileAppSettingToDTO(model.settings)
      }
    case AppKind.PosTef:
      return {
        ...baseMapping,
        type: AppKind.PosTef
      }
    case AppKind.SmartMenu:
      return {
        ...baseMapping,
        type: AppKind.SmartMenu,
        settings: mapSmartMenuAppSettingToDTO(model.settings)
      }
    case AppKind.Other:
      return {
        ...baseMapping,
        type: AppKind.Other
      }
    case AppKind.Store: {
      // `categories` é gerenciado pelo endpoint dedicado `PUT /apps/{id}/categories`
      // (ver `CPAppGateway.upsertCategoriesPreparationTime`). Não enviar no payload de
      // `PUT /apps/{id}` para evitar o `syncCategories` destrutivo do backend.
      return {
        ...baseMapping,
        name: resolveStoreAppInternalName(model),
        type: AppKind.Store,
        settings: mapStoreAppSettingToDTO(model.settings),
        domain: model.domainEditable ? (model.domain ?? null) : undefined
      }
    }
    case AppKind.KdsMobile:
      return {
        ...baseMapping,
        type: AppKind.KdsMobile,
        settings: mapKdsMobileAppSettingToDTO(model.settings)
      }
  }
}

export function mapDTOToApp (dto: AppDTO): App {
  const baseMapping = {
    id: dto.id ?? undefined,
    name: dto.name ?? '',
    imageUrl: dto.image_url ?? undefined,
    terminal: dto.terminal ?? undefined,
    device: dto.device
      ? {
          model: dto.device.model
        }
      : undefined,
    pushNotificationStatus: dto.push_notification_status,
    wasBaseProductAutoEnabledForSale: dto.was_base_product_auto_enabled_for_sale ?? undefined
  }

  switch (dto.type) {
    case AppKind.DigitalMenu:
      return {
        ...baseMapping,
        kind: AppKind.DigitalMenu,
        settings: dto.settings ? mapDTOToDigitalMenuAppSetting(dto.settings) : {}
      } as DigitalMenuApp
    case AppKind.Totem:
      return {
        ...baseMapping,
        kind: AppKind.Totem,
        settings: dto.settings ? mapDTOToTotemAppSetting(dto.settings) : {}
      } as TotemApp

    case AppKind.Desktop:
      return {
        ...baseMapping,
        kind: AppKind.Desktop,
        settings: dto.settings ? mapDTOToDesktopAppSetting(dto.settings) : { modules: [] }
      } as DesktopApp
    case AppKind.Ordersheet:
      return {
        ...baseMapping,
        kind: AppKind.Ordersheet
      } as OrdersheetApp
    case AppKind.PdvMobile:
      return {
        ...baseMapping,
        kind: AppKind.PdvMobile,
        settings: dto.settings ? mapDTOToPdvMobileAppSetting(dto.settings) : {}
      } as PdvMobileApp
    case AppKind.Server:
      return {
        ...baseMapping,
        kind: AppKind.Server,
        settings: dto.settings ? mapDTOToServerAppSetting(dto.settings) : {}
      } as ServerApp
    case AppKind.Mobile:
      return {
        ...baseMapping,
        kind: AppKind.Mobile,
        settings: dto.settings ? mapDTOToMobileAppSetting(dto.settings) : {}
      } as MobileApp
    case AppKind.PosTef:
      return {
        ...baseMapping,
        kind: AppKind.PosTef
      } as PosTefApp
    case AppKind.Other:
      return {
        ...baseMapping,
        kind: AppKind.Other
      } as OtherApp
    case AppKind.SmartMenu:
      return {
        ...baseMapping,
        kind: AppKind.SmartMenu,
        settings: dto.settings ? mapDTOToSmartMenuAppSetting(dto.settings) : {}
      } as SmartMenuApp
    case AppKind.Store: {
      const storeSettings = dto.settings
        ? mapDTOToStoreAppSetting(dto.settings)
        : {
            basicInfo: { name: '' },
            theme: {},
            contact: { channels: [] },
            socialMedia: []
          }

      // A tela "Tempo de preparo adicional" só lida com categorias que têm tempo > 0;
      // categorias com `preparation_time = 0` (auto-inseridas via `handleCategory` quando
      // produtos são vinculados) são filtradas para não poluir a UX.
      return {
        ...baseMapping,
        kind: AppKind.Store,
        settings: storeSettings,
        domain: dto.domain ?? null,
        domainEditable: dto.domain_editable ?? false,
        categories: dto.categories
          ?.flatMap(function flatten (cat): AppCatalogCategoryDTOType[] {
            return [cat, ...(cat.subcategories?.flatMap(flatten) ?? [])]
          })
          .filter(cat => (cat.preparation_time ?? 0) > 0)
          .map(cat => ({
            catalogCategoryId: cat.category?.id,
            catalogCategoryName: cat.category?.name,
            preparationTimeMinutes: cat.preparation_time
          }))
      } as StoreApp
    }
    case AppKind.KdsMobile:
      return {
        ...baseMapping,
        kind: AppKind.KdsMobile,
        settings: dto.settings ? mapDTOToKdsMobileAppSetting(dto.settings) : {}
      } as KdsMobileApp
    default:
      throw new Error(`Invalid app type: ${dto.type}`)
  }
}
